import { EventEmitter, Injectable } from '@angular/core';
import { LocalStorageService } from '../../services/storage/local-storage.service';

export interface DrawStyle {
	strokeColor: string;
	strokeWidth: number;
	fillColor: string;
	fillOpacity: number;
}

export type DrawStyleType = 'line' | 'polygon';

const DEFAULT_LINE_STYLE: DrawStyle = {
	strokeColor: '#3388ff',
	strokeWidth: 3,
	fillColor: '#3388ff',
	fillOpacity: 0
};

const DEFAULT_POLYGON_STYLE: DrawStyle = {
	strokeColor: '#3388ff',
	strokeWidth: 3,
	fillColor: '#3388ff',
	fillOpacity: 0.2
};

const COLOR_PALETTE = [
	'#ff0000', '#ff7f00', '#ffff00', '#99ff00',
	'#00cc00', '#006600', '#00ffff', '#007fff',
	'#0000ff', '#7f00ff', '#ff00ff', '#ff007f',
	'#000000', '#666666', '#999999', '#ffffff',
];

const STROKE_WIDTHS = [1, 2, 3, 4, 5, 6, 8];
const FILL_OPACITIES = [0, 0.1, 0.2, 0.3, 0.5, 0.7, 1.0];

@Injectable({
	providedIn: 'root'
})
export class DrawStyleService {
	public styleChanged = new EventEmitter<{ type: DrawStyleType; style: DrawStyle }>();

	private lineStyle: DrawStyle = { ...DEFAULT_LINE_STYLE };
	private polygonStyle: DrawStyle = { ...DEFAULT_POLYGON_STYLE };

	readonly colorPalette = COLOR_PALETTE;
	readonly strokeWidths = STROKE_WIDTHS;
	readonly fillOpacities = FILL_OPACITIES;

	constructor(private storage: LocalStorageService) {
		this.loadStyles();
	}

	private async loadStyles(): Promise<void> {
		const savedLine = await this.storage.get('drawStyleLine') as DrawStyle;
		const savedPolygon = await this.storage.get('drawStylePolygon') as DrawStyle;
		if (savedLine) {
			this.lineStyle = { ...DEFAULT_LINE_STYLE, ...savedLine };
		}
		if (savedPolygon) {
			this.polygonStyle = { ...DEFAULT_POLYGON_STYLE, ...savedPolygon };
		}
	}

	async saveStyle(type: DrawStyleType): Promise<void> {
		if (type === 'line') {
			await this.storage.set('drawStyleLine', this.lineStyle);
			this.styleChanged.emit({ type: 'line', style: this.lineStyle });
		} else {
			await this.storage.set('drawStylePolygon', this.polygonStyle);
			this.styleChanged.emit({ type: 'polygon', style: this.polygonStyle });
		}
	}

	getStyle(type: DrawStyleType = 'polygon'): DrawStyle {
		return type === 'line' ? { ...this.lineStyle } : { ...this.polygonStyle };
	}

	setStyle(style: Partial<DrawStyle>, type: DrawStyleType = 'polygon'): void {
		if (type === 'line') {
			this.lineStyle = { ...this.lineStyle, ...style };
		} else {
			this.polygonStyle = { ...this.polygonStyle, ...style };
		}
		this.saveStyle(type);
	}

	setStrokeColor(color: string, type: DrawStyleType = 'polygon'): void {
		this.setStyle({ strokeColor: color }, type);
	}

	setStrokeWidth(width: number, type: DrawStyleType = 'polygon'): void {
		this.setStyle({ strokeWidth: width }, type);
	}

	setFillColor(color: string, type: DrawStyleType = 'polygon'): void {
		this.setStyle({ fillColor: color }, type);
	}

	setFillOpacity(opacity: number, type: DrawStyleType = 'polygon'): void {
		this.setStyle({ fillOpacity: opacity }, type);
	}

	getLeafletPathOptions(): L.PathOptions {
		return {
			color: this.polygonStyle.strokeColor,
			weight: this.polygonStyle.strokeWidth,
			fillColor: this.polygonStyle.fillColor,
			fillOpacity: this.polygonStyle.fillOpacity
		};
	}

	getLeafletPolylineOptions(): L.PolylineOptions {
		return {
			color: this.lineStyle.strokeColor,
			weight: this.lineStyle.strokeWidth
		};
	}

	resetToDefault(type: DrawStyleType = 'polygon'): void {
		if (type === 'line') {
			this.lineStyle = { ...DEFAULT_LINE_STYLE };
		} else {
			this.polygonStyle = { ...DEFAULT_POLYGON_STYLE };
		}
		this.saveStyle(type);
	}
}
